import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LoadingSequence = () => {
  const [stage, setStage] = useState(0);
  const [progress, setProgress] = useState(0);

  const stages = [
    { text: 'Initializing MacMind System', emoji: '⚙️', duration: 1500 },
    { text: 'Waking Dragon Agents', emoji: '🐉', duration: 1500 },
    { text: 'Establishing Neural Network', emoji: '🧠', duration: 2000 },
  ];

  useEffect(() => {
    // Progress bar animation
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) return 100;
        return prev + 2;
      });
    }, 100);

    // Stage transitions
    const stageTimers = [
      setTimeout(() => setStage(1), 1500),
      setTimeout(() => setStage(2), 3000),
    ];

    return () => {
      clearInterval(progressInterval);
      stageTimers.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-dark-bg flex items-center justify-center">
      {/* Background Effect */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-1/2 left-1/2 w-[800px] h-[800px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
          }}
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {/* Main Content */}
      <div className="relative z-10 text-center px-4">
        {/* Logo/Icon */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="mb-12"
        >
          <div className="relative inline-block">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-full blur-3xl opacity-50 animate-pulse" />
            <svg
              className="w-24 h-24 md:w-32 md:h-32 relative z-10 text-blue-400"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M21.33 8.6c-1.67-1.67-3.77-2.6-5.94-2.6-1.48 0-2.9.38-4.11 1.09C10.07 6.38 8.65 6 7.17 6c-2.17 0-4.27.93-5.94 2.6-1.67 1.67-2.6 3.77-2.6 5.94 0 1.48.38 2.9 1.09 4.11C.38 19.86 0 21.28 0 22.76v.01c0 .41.34.75.75.75h22.5c.41 0 .75-.34.75-.75v-.01c0-1.48-.38-2.9-1.09-4.11.71-1.21 1.09-2.63 1.09-4.11 0-2.17-.93-4.27-2.6-5.94zm-14.16 0c1.11-1.11 2.51-1.67 3.94-1.67.98 0 1.92.24 2.76.7-.84.45-1.63 1.02-2.33 1.72-1.67 1.67-2.6 3.77-2.6 5.94 0 .99.17 1.95.48 2.85-.84-.45-1.63-1.02-2.33-1.72-1.11-1.11-1.67-2.51-1.67-3.94s.56-2.83 1.67-3.94zm7.94 7.94c-1.11 1.11-2.51 1.67-3.94 1.67-.98 0-1.92-.24-2.76-.7.84-.45 1.63-1.02 2.33-1.72 1.67-1.67 2.6-3.77 2.6-5.94 0-.99-.17-1.95-.48-2.85.84.45 1.63 1.02 2.33 1.72 1.11 1.11 1.67 2.51 1.67 3.94s-.56 2.83-1.67 3.94z" />
            </svg>
          </div>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="text-5xl md:text-7xl font-black text-white mb-8 text-glow"
        >
          MacMind
        </motion.h1>

        {/* Stage Display */}
        <div className="h-24 mb-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={stage}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col items-center gap-3"
            >
              <div className="text-5xl animate-pulse">{stages[stage].emoji}</div>
              <p className="text-xl md:text-2xl text-cyan-400 font-medium">
                {stages[stage].text}
              </p>
              <div className="flex gap-1">
                {[...Array(3)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="w-2 h-2 rounded-full bg-cyan-400"
                    animate={{
                      opacity: [0.3, 1, 0.3],
                    }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      delay: i * 0.2,
                    }}
                  />
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Progress Bar */}
        <div className="w-full max-w-md mx-auto">
          <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full"
              initial={{ width: '0%' }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.1 }}
            />
          </div>
          <motion.p
            className="text-sm text-gray-500 mt-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            {Math.floor(progress)}%
          </motion.p>
        </div>

        {/* Hint Text */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="text-sm text-gray-600 mt-8"
        >
          Preparing your network intelligence dashboard...
        </motion.p>
      </div>
    </div>
  );
};

export default LoadingSequence;
